import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Edit, Save, Eye, Trash2, FileText, StickyNote, Mail, Search,
  UserCheck, UserX, ToggleLeft, ToggleRight, Download, Briefcase,
  Bookmark, X, Archive, ArchiveRestore, UserPlus, ShieldOff, ShieldAlert, AlertTriangle,
  ChevronLeft, ChevronRight, HardHat, CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { JOB_PREFERENCES, CACES_CATEGORIES } from "@/constants";
import CandidateCacesCard from "./CandidateCacesCard";
import VivierAiSearchBar from "./VivierAiSearchBar";
import { useJobPreferences } from "@/hooks/useJobPreferences";
import { api, API_BASE_URL } from "./adminApi";
import ComplianceChecklist, { ComplianceBadge, computeCompliance } from "./ComplianceChecklist";
import SsnInput from "@/components/SsnInput";
import CandidateSplitPreviewDialog from "./CandidateSplitPreviewDialog";
import CVAnonymizer from "./CVAnonymizer";
import RejectedDocsEmailDialog from "./RejectedDocsEmailDialog";
import CandidateMissionsTimeline from "./CandidateMissionsTimeline";
import AdminUploadDocButton from "./AdminUploadDocButton";
import EditDocumentDialog from "./EditDocumentDialog";
import MultiSelect from "./MultiSelect";
import AiExtractPanel from "./AiExtractPanel";
import CreateCandidateDialog from "./CreateCandidateDialog";
import NationalitySelect from "@/components/NationalitySelect";
import PhoneInputFr from "@/components/PhoneInputFr";
import PhoneClickable from "@/components/PhoneClickable";
import { isEuEeaCh } from "@/lib/eu_nationalities";

/**
 * A candidate is flagged as "foreign national" (ressortissant étranger) when:
 * - their ID document is a "carte_sejour" (residence permit), OR
 * - their declared nationality is set and is not French ("Française").
 * This is purely informational/administrative (no rights restriction).
 */
function isForeignNational(c) {
  if (!c) return false;
  if (c.id_doc_type === "carte_sejour") return true;
  const nat = (c.nationality || "").trim();
  if (!nat) return false;
  // EU/EEA/CH citizens are not "prefecture-verifiable" — treat as domestic.
  if (isEuEeaCh(nat)) return false;
  return true;
}

function MissionEmailButton({ candidateId }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ mission_title: "", mission_location: "", mission_start: "" });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!form.mission_title || !form.mission_start) { toast.error("Titre et date de début requis"); return; }
    setSending(true);
    try {
      await api.post("/admin/email/mission", { user_id: candidateId, ...form });
      toast.success("Email de confirmation de mission envoyé");
      setShow(false); setForm({ mission_title: "", mission_location: "", mission_start: "" });
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };

  if (!show) {
    return (
      <Button variant="outline" className="rounded-none border-[#0069B4] text-[#0069B4] text-xs" onClick={() => setShow(true)} data-testid="email-mission-btn">
        Confirmation de mission
      </Button>
    );
  }

  return (
    <div className="w-full bg-[#E6F0F7] border border-[#0069B4]/20 p-4 mt-2 space-y-3" data-testid="mission-form">
      <h5 className="text-xs font-bold text-[#0069B4]">Confirmation de mission</h5>
      <div className="grid grid-cols-2 gap-2">
        <Input value={form.mission_title} onChange={(e) => setForm({ ...form, mission_title: e.target.value })} placeholder="Titre de la mission *" className="rounded-none text-xs h-8" data-testid="mission-title" />
        <Input value={form.mission_location} onChange={(e) => setForm({ ...form, mission_location: e.target.value })} placeholder="Lieu" className="rounded-none text-xs h-8" data-testid="mission-location" />
      </div>
      <Input type="date" value={form.mission_start} onChange={(e) => setForm({ ...form, mission_start: e.target.value })} className="rounded-none text-xs h-8" data-testid="mission-start" />
      <div className="flex gap-2">
        <Button onClick={handleSend} disabled={sending} className="bg-[#0069B4] text-white rounded-none text-xs h-8" data-testid="send-mission-email">{sending ? "Envoi..." : "Envoyer"}</Button>
        <Button variant="ghost" onClick={() => setShow(false)} className="rounded-none text-xs h-8">Annuler</Button>
      </div>
    </div>
  );
}

export default function CandidatesTab({ initialFilter }) {
  const { prefs: dynamicPrefs } = useJobPreferences();
  const JOB_PREFS = dynamicPrefs.length ? dynamicPrefs : JOB_PREFERENCES;
  const [candidates, setCandidates] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [renameDoc, setRenameDoc] = useState(null); // doc opened in the edit/rename dialog
  const [quickNote, setQuickNote] = useState(""); // text of the quick note field (Notes rapides)
  const uploadDocRef = useRef(null);
  const [anonymizeDoc, setAnonymizeDoc] = useState(null);
  const [rejectEmailOpen, setRejectEmailOpen] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminCaces, setAdminCaces] = useState({});
  const [adminCacesExpiry, setAdminCacesExpiry] = useState({}); // { caces_key: "YYYY-MM-DD" }
  const [candidateForm, setCandidateForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterCaces, setFilterCaces] = useState([]);
  const [filterPrefs, setFilterPrefs] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterDistance, setFilterDistance] = useState("");
  const [filterCompleteness, setFilterCompleteness] = useState("all"); // "all" | "complete" | "incomplete"
  const [filterRole, setFilterRole] = useState("all"); // "all" | "candidate" | "employee"
  const [filterMission, setFilterMission] = useState("all"); // "all" | "yes" | "no"
  const [filterLastLogin, setFilterLastLogin] = useState("all"); // "all" | "<7" | "<30" | "<90" | "never"
  const [filterStatus, setFilterStatus] = useState("active"); // "active" | "inactive" | "archived" | "all"
  const [filterAgency, setFilterAgency] = useState("all"); // "all" | "__none__" | <agency_id>
  const [filterOrder, setFilterOrder] = useState("all"); // "all" | "__none__" | <order_id>
  const [orders, setOrders] = useState([]);
  const [filterForeignOnly, setFilterForeignOnly] = useState(false); // ressortissants étrangers
  const [filterPrefecture, setFilterPrefecture] = useState("all"); // "all" | "ok" | "todo"
  const [filterAvailableOn, setFilterAvailableOn] = useState(""); // date ISO YYYY-MM-DD
  // Saved filter presets
  const [savedFilters, setSavedFilters] = useState([]);
  const [activePresetId, setActivePresetId] = useState("");
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  // Semantic AI search (Claude)
  const [aiQuery, setAiQuery] = useState("");
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSummary, setAiSummary] = useState(""); // human-readable summary of active AI filters

  const fetchAgencies = useCallback(async () => { try { const r = await api.get("/agencies"); setAgencies(r.data); } catch {} }, []);
  const fetchOrders = useCallback(async () => { try { const r = await api.get("/admin/orders"); setOrders(r.data); } catch {} }, []);
  const fetchSavedFilters = useCallback(async () => {
    try { const r = await api.get("/admin/candidate-filters"); setSavedFilters(r.data); } catch {}
  }, []);

  const applyPreset = (presetId) => {
    setActivePresetId(presetId);
    if (!presetId) {
      setSearchText(""); setFilterCaces([]); setFilterPrefs([]);
      setFilterCity(""); setFilterDistance(""); setFilterCompleteness("all");
      return;
    }
    const p = savedFilters.find((x) => x.preset_id === presetId);
    if (!p) return;
    const f = p.filters || {};
    setSearchText(f.search || "");
    setFilterCaces(f.caces || []);
    setFilterPrefs(f.preferences || []);
    setFilterCity(f.city || "");
    setFilterDistance(f.distance || "");
    setFilterCompleteness(f.completeness || "all");
    toast.success(`Filtre « ${p.name} » appliqué`);
  };

  const saveCurrentAsPreset = async () => {
    const name = newPresetName.trim();
    if (!name) { toast.error("Nom requis"); return; }
    try {
      await api.post("/admin/candidate-filters", {
        name,
        filters: {
          search: searchText, caces: filterCaces, preferences: filterPrefs,
          city: filterCity, distance: filterDistance, completeness: filterCompleteness,
        },
      });
      toast.success(`Filtre « ${name} » sauvegardé`);
      setShowSavePresetDialog(false);
      setNewPresetName("");
      fetchSavedFilters();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm("Supprimer ce filtre sauvegardé ?")) return;
    try {
      await api.delete(`/admin/candidate-filters/${presetId}`);
      toast.success("Filtre supprimé");
      if (activePresetId === presetId) setActivePresetId("");
      fetchSavedFilters();
    } catch { toast.error("Erreur"); }
  };

  const runAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiSearching(true);
    try {
      const r = await api.post("/admin/candidates/semantic-search", { query: aiQuery.trim() });
      const f = r.data?.filters || {};
      setFilterPrefs(f.preferences || []);
      setFilterCaces(f.caces || []);
      setFilterCity(f.city || "");
      setFilterDistance(f.distance_km ? String(f.distance_km) : "");
      setFilterAvailableOn(f.available_on || "");
      setFilterRole(f.role === "all" ? "all" : (f.role || "all"));
      setFilterMission(f.in_mission === "all" ? "all" : (f.in_mission || "all"));
      setFilterForeignOnly(!!f.foreign_only);
      setFilterPrefecture(f.prefecture === "all" ? "all" : (f.prefecture || "all"));
      setSearchText(f.search || "");
      setAiSummary(f.summary || "");
      setActivePresetId("");
      toast.success("Filtres appliqués depuis l'IA");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur IA");
    } finally { setAiSearching(false); }
  };

  const clearAiSearch = () => { setAiQuery(""); setAiSummary(""); };

  const fetchCandidates = useCallback(async () => {
    const params = {};
    if (searchText) params.search = searchText;
    if (filterCaces.length) params.caces = filterCaces.join(",");
    if (filterPrefs.length) params.preferences = filterPrefs.join(",");
    if (filterCity && filterDistance) { params.city = filterCity; params.distance = filterDistance; }
    if (filterRole !== "all") params.role = filterRole;
    if (filterMission !== "all") params.in_mission = filterMission;
    if (filterStatus !== "all") params.status = filterStatus;
    if (filterAgency !== "all") params.agency_id = filterAgency;
    if (filterOrder !== "all") params.order_id = filterOrder;
    try { const r = await api.get("/candidates", { params }); setCandidates(r.data); } catch {}
  }, [searchText, filterCaces, filterPrefs, filterCity, filterDistance, filterRole, filterMission, filterStatus, filterAgency, filterOrder]);

  useEffect(() => { fetchAgencies(); fetchSavedFilters(); fetchOrders(); }, [fetchAgencies, fetchSavedFilters, fetchOrders]);
  useEffect(() => { const t = setTimeout(fetchCandidates, 300); return () => clearTimeout(t); }, [fetchCandidates]);

  const toggleRole = async (userId, currentRole) => { const r = currentRole === "candidate" ? "employee" : "candidate"; try { await api.patch(`/candidates/${userId}/role`, { role: r }); toast.success(`Rôle mis à jour : ${r === "employee" ? "Salarié" : "Candidat"}`); fetchCandidates(); } catch { toast.error("Erreur"); } };
  const toggleStatus = async (userId, currentStatus) => { const s = currentStatus === "active" ? "inactive" : "active"; try { await api.patch(`/candidates/${userId}/status`, { status: s }); toast.success(`Statut mis à jour : ${s}`); fetchCandidates(); } catch { toast.error("Erreur"); } };
  const archiveCandidate = async (userId, name) => {
    if (!window.confirm(`Archiver « ${name} » ?\n\nLe salarié n'apparaîtra plus dans la liste par défaut mais ses données seront conservées (utile pour l'historique).`)) return;
    try { await api.patch(`/candidates/${userId}/status`, { status: "archived" }); toast.success("Salarié archivé"); fetchCandidates(); } catch { toast.error("Erreur"); }
  };
  const unarchiveCandidate = async (userId) => {
    try { await api.patch(`/candidates/${userId}/status`, { status: "active" }); toast.success("Salarié réactivé"); fetchCandidates(); } catch { toast.error("Erreur"); }
  };

  // Apply filters pushed from StatsTab (KPI cards / recent views) via initialFilter prop.
  // `version` field changes on every push so even repeated clicks re-trigger the effect.
  useEffect(() => {
    if (!initialFilter || !initialFilter.filters) return;
    const f = initialFilter.filters;
    if (f.role !== undefined) setFilterRole(f.role);
    if (f.in_mission !== undefined) setFilterMission(f.in_mission);
    if (f.status !== undefined) setFilterStatus(f.status);
    if (f.agency_id !== undefined) setFilterAgency(f.agency_id);
    if (f.order_id !== undefined) setFilterOrder(f.order_id);
    if (f.last_login !== undefined) setFilterLastLogin(f.last_login);
    if (f.preferences !== undefined) setFilterPrefs(f.preferences);
    if (f.caces !== undefined) setFilterCaces(f.caces);
    if (f.search !== undefined) setSearchText(f.search);
    if (f.foreign_only) {
      // No backend filter — client-side flag handled below
      setFilterForeignOnly(true);
    } else if (f.foreign_only === false) {
      setFilterForeignOnly(false);
    }
    // Special case: auto-open a specific candidate's profile (used by
    // ApplicationsTab and OffersTab to deep-link to the fiche).
    if (f.open_user_id) {
      viewCandidate(f.open_user_id);
    }
  }, [initialFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewCandidate = async (userId) => {
    try {
      const r = await api.get(`/candidates/${userId}`);
      setSelectedCandidate(r.data);
      setAdminNotes(r.data.notes_admin || "");
      setAdminCaces(r.data.caces || {});
      setAdminCacesExpiry(r.data.caces_expiry || {});
      setCandidateForm({
        first_name: r.data.first_name || "",
        last_name: r.data.last_name || "",
        name: r.data.name || "",
        email: r.data.email || "",
        phone: r.data.phone || "",
        address: r.data.address || "",
        postal_code: r.data.postal_code || "",
        city: r.data.city || "",
        experience: r.data.experience || "",
        disponibilite_debut: r.data.disponibilite_debut || "",
        disponibilite_fin: r.data.disponibilite_fin || "",
        agency_id: r.data.agency_id || "",
        mobility_km: r.data.mobility_km || 30,
        civility: r.data.civility || "",
        nationality: r.data.nationality || "",
        birth_date: r.data.birth_date || "",
        birth_place: r.data.birth_place || "",
        birth_name: r.data.birth_name || "",
        marital_status: r.data.marital_status || "",
        social_security_number: r.data.social_security_number || "",
        id_doc_type: r.data.id_doc_type || "",
        id_doc_validity_start: r.data.id_doc_validity_start || "",
        id_doc_validity_end: r.data.id_doc_validity_end || "",
        id_doc_origin: r.data.id_doc_origin || "",
        id_doc_department: r.data.id_doc_department || "",
        id_doc_issuer: r.data.id_doc_issuer || "",
        id_doc_number: r.data.id_doc_number || "",
        id_doc_issuer_code: r.data.id_doc_issuer_code || "",
        iban: r.data.iban || "",
        bic: r.data.bic || "",
        bank_domiciliation: r.data.bank_domiciliation || "",
        emergency_contact_name: r.data.emergency_contact_name || "",
        emergency_contact_phone: r.data.emergency_contact_phone || "",
        emergency_contact_relation: r.data.emergency_contact_relation || "",
        anael_qualification: r.data.anael_qualification || "",
        anael_category: r.data.anael_category || "",
        prefecture_verified: !!r.data.prefecture_verified,
        epi_safety_test_ok: !!r.data.epi_safety_test_ok,
        epi_distributed_date: r.data.epi_distributed_date || "",
        references_check_ok: !!r.data.references_check_ok,
        sms_operational_consent: !!r.data.sms_operational_consent,
        sms_marketing_consent: !!r.data.sms_marketing_consent,
      });
    } catch { toast.error("Erreur"); }
  };

  const saveCandidateProfile = async () => {
    if (!selectedCandidate) return;
    // SSN sanity check — 15 digits, starts with 1 if male, 2 otherwise.
    const ssn = (candidateForm.social_security_number || "").replace(/\D/g, "");
    if (ssn) {
      if (ssn.length !== 15) {
        toast.error("Le numéro de sécurité sociale doit contenir 15 chiffres.");
        return;
      }
      const civ = (candidateForm.civility || "").toLowerCase();
      const isMale = civ === "monsieur" || civ === "m." || civ === "m";
      const isFemale = civ === "madame" || civ === "mme" || civ === "mademoiselle" || civ === "mlle";
      const expectedPrefix = isMale ? "1" : (isFemale ? "2" : null);
      if (expectedPrefix && !ssn.startsWith(expectedPrefix)) {
        const cont = window.confirm(`Le numéro SS commence par ${ssn[0]} alors qu'il devrait commencer par ${expectedPrefix} pour ${isMale ? "un homme" : "une femme"}. Enregistrer quand même ?`);
        if (!cont) return;
      }
    }
    setSavingProfile(true);
    try {
      const r = await api.patch(`/candidates/${selectedCandidate.user_id}/profile`, candidateForm);
      setSelectedCandidate({ ...selectedCandidate, ...r.data });
      // Also save admin notes alongside the profile to avoid 2 clicks
      try {
        await api.patch(`/candidates/${selectedCandidate.user_id}/admin-info`, { notes_admin: adminNotes, caces: adminCaces, caces_expiry: adminCacesExpiry });
      } catch { /* notes save is best-effort; profile already saved */ }
      toast.success("Profil candidat mis à jour");
      fetchCandidates();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingProfile(false); }
  };

  const saveAdminInfo = async () => {
    if (!selectedCandidate) return;
    setSavingInfo(true);
    try {
      await api.patch(`/candidates/${selectedCandidate.user_id}/admin-info`, { notes_admin: adminNotes, caces: adminCaces, caces_expiry: adminCacesExpiry });
      toast.success("Informations admin sauvegardées");
    } catch { toast.error("Erreur"); }
    finally { setSavingInfo(false); }
  };

  // Client-side filtered list — hoisted to a useMemo so it can be reused by:
  //  - the count label
  //  - the row rendering
  //  - the prev/next navigation from the candidate profile dialog
  const filteredCandidates = useMemo(() => {
    const lastLoginCutoff = (days) => Date.now() - days * 86400000;
    const matchesLastLogin = (c) => {
      if (filterLastLogin === "all") return true;
      const ts = c.last_login_at ? Date.parse(c.last_login_at) : 0;
      if (filterLastLogin === "never") return !ts;
      const days = { "<7": 7, "<30": 30, "<90": 90 }[filterLastLogin];
      return ts > 0 && ts >= lastLoginCutoff(days);
    };
    const matchesAvailability = (c) => {
      if (!filterAvailableOn) return true;
      const debut = c.disponibilite_debut || "";
      const fin = c.disponibilite_fin || "";
      if (!debut && !fin) return false;
      if (debut && filterAvailableOn < debut) return false;
      if (fin && filterAvailableOn > fin) return false;
      return true;
    };
    return candidates.filter((c) => {
      if (!matchesLastLogin(c)) return false;
      if (!matchesAvailability(c)) return false;
      if (filterForeignOnly && !isForeignNational(c)) return false;
      if (filterPrefecture === "ok" && !c.prefecture_verified) return false;
      if (filterPrefecture === "todo" && (!isForeignNational(c) || c.prefecture_verified)) return false;
      if (filterCompleteness === "all") return true;
      const comp = computeCompliance(c.doc_types || [], c.role);
      const isComplete = comp.provided === comp.total;
      return filterCompleteness === "complete" ? isComplete : !isComplete;
    });
  }, [candidates, filterLastLogin, filterAvailableOn, filterForeignOnly, filterPrefecture, filterCompleteness]);

  // Prev/Next navigation within the filtered list (only relevant when a candidate dialog is open).
  const currentFilteredIdx = selectedCandidate ? filteredCandidates.findIndex((c) => c.user_id === selectedCandidate.user_id) : -1;
  const prevCandidateId = currentFilteredIdx > 0 ? filteredCandidates[currentFilteredIdx - 1].user_id : null;
  const nextCandidateId = currentFilteredIdx >= 0 && currentFilteredIdx < filteredCandidates.length - 1 ? filteredCandidates[currentFilteredIdx + 1].user_id : null;

  // Quick note: append a timestamped entry to notes_admin (kept as text with linebreaks).
  const addQuickNote = async () => {
    const txt = quickNote.trim();
    if (!txt || !selectedCandidate) return;
    const stamp = new Date().toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const entry = `[${stamp}] ${txt}`;
    const merged = adminNotes ? `${adminNotes.trimEnd()}\n${entry}` : entry;
    setAdminNotes(merged);
    setQuickNote("");
    try {
      await api.patch(`/candidates/${selectedCandidate.user_id}/admin-info`, { notes_admin: merged });
      toast.success("Note ajoutée");
    } catch { toast.error("Erreur"); setAdminNotes(adminNotes); }
  };

  return (
    <div>
      {/* Search & Filters */}
      <div className="bg-white border border-gray-200 p-4 mb-4 space-y-3">
        {/* Saved presets bar */}
        {savedFilters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-gray-100">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mr-1">Filtres sauvegardés :</span>
            {savedFilters.map((p) => (
              <div key={p.preset_id} className={`group inline-flex items-center gap-1 ${activePresetId === p.preset_id ? "bg-[#662483] text-white" : "bg-[#F0E9F3] text-[#662483]"} text-xs`} data-testid={`preset-${p.preset_id}`}>
                <button type="button" onClick={() => applyPreset(p.preset_id)} className="px-2 py-1 hover:bg-[#662483] hover:text-white font-semibold transition-colors" title={`Appliquer "${p.name}"`}>
                  {p.name}
                </button>
                <button type="button" onClick={() => deletePreset(p.preset_id)} className="px-1 py-1 hover:bg-red-500 hover:text-white transition-colors" title="Supprimer" data-testid={`preset-delete-${p.preset_id}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {activePresetId && (
              <button type="button" onClick={() => applyPreset("")} className="text-[10px] text-red-500 hover:underline ml-1">
                Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Row 0 : Semantic AI search (Claude) */}
        <VivierAiSearchBar
          query={aiQuery}
          setQuery={setAiQuery}
          onSearch={runAiSearch}
          onClear={clearAiSearch}
          searching={aiSearching}
          summary={aiSummary}
        />

        {/* Row 1 : Search (wide) */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Rechercher (nom, email, notes, ville…)"
            className="rounded-none pl-10 text-sm h-9"
            data-testid="search-candidates"
          />
        </div>

        {/* Row 2 : 4 dropdowns — Préférences / CACES / Complétude / Préfecture */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Préférences métier</Label>
            <MultiSelect
              options={JOB_PREFS.map((p) => ({ key: p.key, label: p.label }))}
              value={filterPrefs}
              onChange={setFilterPrefs}
              placeholder="Tous métiers"
              dataTestId="filter-prefs"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">CACES</Label>
            <MultiSelect
              options={CACES_CATEGORIES.map((c) => ({ key: c.key, label: c.label }))}
              value={filterCaces}
              onChange={setFilterCaces}
              placeholder="Tous CACES"
              dataTestId="filter-caces"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Complétude du dossier</Label>
            <Select value={filterCompleteness} onValueChange={setFilterCompleteness}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-completeness"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les candidats</SelectItem>
                <SelectItem value="complete">Dossiers complets (5/5)</SelectItem>
                <SelectItem value="incomplete">Dossiers incomplets</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Préfecture (ressortissants étrangers)</Label>
            <Select value={filterPrefecture} onValueChange={setFilterPrefecture}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-prefecture"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="ok">Vérification OK</SelectItem>
                <SelectItem value="todo">À faire</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 3 : 4 dropdowns — Rôle / Mission / Dernière connexion / Statut */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Rôle</Label>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="candidate">Candidats uniquement</SelectItem>
                <SelectItem value="employee">Salariés uniquement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Mission</Label>
            <Select value={filterMission} onValueChange={setFilterMission}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-mission"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="yes">En mission</SelectItem>
                <SelectItem value="no">Sans mission</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Dernière connexion</Label>
            <Select value={filterLastLogin} onValueChange={setFilterLastLogin}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-last-login"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="<7">Moins de 7 jours</SelectItem>
                <SelectItem value="<30">Moins de 30 jours</SelectItem>
                <SelectItem value="<90">Moins de 90 jours</SelectItem>
                <SelectItem value="never">Jamais connecté</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Statut du compte</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
                <SelectItem value="archived">Archivés</SelectItem>
                <SelectItem value="all">Tous</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Agence de rattachement</Label>
            <Select value={filterAgency} onValueChange={setFilterAgency}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-agency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-agency-opt-all">Toutes les agences</SelectItem>
                <SelectItem value="__none__" data-testid="filter-agency-opt-__none__">Sans agence renseignée</SelectItem>
                {agencies.map((a) => (
                  <SelectItem key={a.agency_id} value={a.agency_id} data-testid={`filter-agency-opt-${a.agency_id}`}>
                    {a.name}{a.city ? ` — ${a.city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Commande</Label>
            <Select value={filterOrder} onValueChange={setFilterOrder}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="filter-order"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-order-opt-all">Toutes commandes</SelectItem>
                <SelectItem value="__none__" data-testid="filter-order-opt-__none__">Sans commande</SelectItem>
                {orders.map((o) => (
                  <SelectItem key={o.order_id} value={o.order_id} data-testid={`filter-order-opt-${o.order_id}`}>
                    {o.title}{o.client_company ? ` — ${o.client_company}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 4 : Ville + Distance + Dispo date + Foreign toggle */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Ville de référence</Label>
            <Input value={filterCity} onChange={(e) => setFilterCity(e.target.value)} placeholder="Ex: Paris" className="rounded-none text-sm h-9" data-testid="filter-city" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Distance max (km)</Label>
            <Input type="number" value={filterDistance} onChange={(e) => setFilterDistance(e.target.value)} placeholder="Ex: 50" className="rounded-none text-sm h-9" data-testid="filter-distance" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Disponible le</Label>
            <Input type="date" value={filterAvailableOn} onChange={(e) => setFilterAvailableOn(e.target.value)} className="rounded-none text-sm h-9" data-testid="filter-available-on" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[#3F3F3F] cursor-pointer select-none h-9" data-testid="filter-foreign-toggle">
            <Checkbox checked={filterForeignOnly} onCheckedChange={(v) => setFilterForeignOnly(!!v)} />
            <span>Ressortissants étrangers uniquement</span>
          </label>
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100 flex-wrap">
          {(searchText || filterCaces.length || filterPrefs.length || filterCity || filterCompleteness !== "all" || filterRole !== "all" || filterMission !== "all" || filterLastLogin !== "all" || filterStatus !== "active" || filterAgency !== "all" || filterOrder !== "all" || filterForeignOnly || filterPrefecture !== "all" || filterAvailableOn) && (
            <Button variant="ghost" className="text-xs text-red-500 h-7" onClick={() => { setSearchText(""); setFilterCaces([]); setFilterPrefs([]); setFilterCity(""); setFilterDistance(""); setFilterCompleteness("all"); setFilterRole("all"); setFilterMission("all"); setFilterLastLogin("all"); setFilterStatus("active"); setFilterAgency("all"); setFilterOrder("all"); setFilterForeignOnly(false); setFilterPrefecture("all"); setFilterAvailableOn(""); setActivePresetId(""); }} data-testid="clear-filters">
              <X className="w-3 h-3 mr-1" /> Effacer les filtres
            </Button>
          )}
          <Button
            variant="outline"
            disabled={!searchText && !filterCaces.length && !filterPrefs.length && !filterCity && filterCompleteness === "all"}
            className="text-xs h-7 border-[#662483] text-[#662483] hover:bg-[#F0E9F3] rounded-none"
            onClick={() => setShowSavePresetDialog(true)}
            data-testid="save-preset-btn"
          >
            <Bookmark className="w-3 h-3 mr-1" /> Sauvegarder ce filtre
          </Button>
        </div>
      </div>

      {/* Save preset dialog */}
      <Dialog open={showSavePresetDialog} onOpenChange={setShowSavePresetDialog}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Sauvegarder ce filtre</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-gray-500">Vous pourrez réappliquer ce filtre en un clic ultérieurement.</p>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Nom du filtre</Label>
              <Input
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder="Ex: Caristes CACES 3 Paris 50km"
                className="rounded-none"
                autoFocus
                data-testid="preset-name-input"
                onKeyDown={(e) => { if (e.key === "Enter") saveCurrentAsPreset(); }}
              />
            </div>
            <div className="bg-[#FAFAFA] border border-gray-100 p-2 text-[11px] text-gray-600">
              <p className="font-semibold mb-1">Filtres actuels :</p>
              <ul className="space-y-0.5">
                {searchText && <li>• Recherche : <strong>{searchText}</strong></li>}
                {filterPrefs.length > 0 && <li>• Métiers : <strong>{filterPrefs.length}</strong> sélectionné(s)</li>}
                {filterCaces.length > 0 && <li>• CACES : <strong>{filterCaces.length}</strong> sélectionné(s)</li>}
                {filterCity && <li>• Ville : <strong>{filterCity}</strong> {filterDistance && `(≤${filterDistance} km)`}</li>}
                {filterCompleteness !== "all" && <li>• Complétude : <strong>{filterCompleteness === "complete" ? "Complets" : "Incomplets"}</strong></li>}
              </ul>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveCurrentAsPreset} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none flex-1" data-testid="preset-save-confirm">
                <Save className="w-3.5 h-3.5 mr-1" /> Sauvegarder
              </Button>
              <Button variant="outline" onClick={() => setShowSavePresetDialog(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {`${filteredCandidates.length} candidat(s) / salarié(s)`}
        </p>
        <div className="flex gap-2">
          <Button
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-8"
            onClick={() => setCreateOpen(true)}
            data-testid="create-candidate-btn"
            title="Créer un nouveau candidat ou salarié depuis le backoffice"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Créer un candidat
          </Button>
          <Button
            variant="outline"
            className="rounded-none border-[#662483] text-[#662483] hover:bg-[#F0E9F3] text-xs h-8"
            onClick={() => {
              const a = document.createElement("a");
              a.href = `${API_BASE_URL}/api/candidates/export?format=xlsx`;
              a.click();
              toast.success("Export Excel (logo + filigrane FR INTERIM) en téléchargement…");
            }}
            data-testid="export-candidates-xlsx"
            title="Excel avec logo FR INTERIM, en-tête confidentialité et page d'avertissement RGPD — idéal pour transmission à un partenaire"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Excel brandé (.xlsx)
          </Button>
          <Button
            variant="outline"
            className="rounded-none border-[#0069B4] text-[#0069B4] hover:bg-[#E6F0F7] text-xs h-8"
            onClick={() => {
              const a = document.createElement("a");
              a.href = `${API_BASE_URL}/api/candidates/export?format=csv`;
              a.click();
              toast.success("Export CSV en téléchargement…");
            }}
            data-testid="export-candidates-csv"
            title="CSV brut (sans logo)"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV brut
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {(() => {
          const filteredList = filteredCandidates;
          return filteredList.map((c) => {
            const lastLogin = c.last_login_at ? new Date(c.last_login_at) : null;
            const lastLoginLabel = lastLogin ? lastLogin.toLocaleDateString("fr-FR") : "Jamais";
            return (
              <div key={c.user_id} className="bg-white border border-gray-200 p-4 flex items-center justify-between gap-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`admin-candidate-${c.user_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => viewCandidate(c.user_id)}
                      className="text-sm font-bold text-[#3F3F3F] hover:text-[#662483] hover:underline transition-colors text-left"
                      data-testid={`open-candidate-${c.user_id}`}
                    >
                      {c.name}
                    </button>
                    <Badge className={`rounded-none text-xs ${c.role === "employee" ? "bg-[#F0E9F3] text-[#662483]" : "bg-[#E6F0F7] text-[#0069B4]"}`}>{c.role === "employee" ? "Salarié" : "Candidat"}</Badge>
                    {/* Only show status badge for non-active statuses (archived/inactive) — active is the default and adds noise */}
                    {c.status === "archived" && <Badge className="rounded-none text-xs bg-gray-200 text-gray-700">Archivé</Badge>}
                    {c.status === "inactive" && <Badge className="rounded-none text-xs bg-red-100 text-red-800">Inactif</Badge>}
                    <ComplianceBadge docTypes={c.doc_types || []} role={c.role} />
                    {c.in_mission && (
                      <Badge className="rounded-none text-xs bg-amber-100 text-amber-800" title={c.current_mission?.title}>
                        🟢 En mission{c.current_mission?.offer_number ? ` · ${c.current_mission.offer_number}` : ""}
                      </Badge>
                    )}
                    {isForeignNational(c) && (
                      <Badge
                        className="rounded-none text-xs bg-blue-100 text-blue-800 border border-blue-300"
                        title={`Ressortissant étranger — ${c.id_doc_type === "carte_sejour" ? "Carte de séjour" : (c.nationality || c.id_doc_origin || "Origine étrangère")}`}
                        data-testid={`foreign-badge-${c.user_id}`}
                      >
                        🌍 Étranger
                      </Badge>
                    )}
                    {isForeignNational(c) && (
                      c.prefecture_verified ? (
                        <Badge className="rounded-none text-xs bg-green-100 text-green-800 border border-green-300" title={`Vérification préfecture effectuée${c.prefecture_verified_at ? " le " + new Date(c.prefecture_verified_at).toLocaleDateString("fr-FR") : ""}`} data-testid={`prefecture-ok-${c.user_id}`}>
                          🏛 Préfecture ✓
                        </Badge>
                      ) : (
                        <Badge className="rounded-none text-xs bg-red-100 text-red-700 border border-red-300" title="Démarches préfecture à effectuer pour ce ressortissant étranger" data-testid={`prefecture-todo-${c.user_id}`}>
                          🏛 Préfecture à faire
                        </Badge>
                      )
                    )}
                    {c.extraction_status === "ok" && (
                      <Badge
                        className="rounded-none text-xs bg-amber-100 text-amber-800 border border-amber-300"
                        title="Données détectées par IA — à valider depuis la fiche"
                        data-testid={`ai-pending-${c.user_id}`}
                      >
                        ✨ IA à valider
                      </Badge>
                    )}
                    {(c.extraction_status === "queued" || c.extraction_status === "processing") && (
                      <Badge className="rounded-none text-xs bg-blue-100 text-blue-700 border border-blue-300" title="Analyse IA en cours">
                        ⏳ IA…
                      </Badge>
                    )}
                    {c.extraction_status === "timeout" && (
                      <Badge
                        className="rounded-none text-xs bg-orange-100 text-orange-800 border border-orange-300"
                        title="L'analyse IA a expiré — relancer depuis la fiche"
                        data-testid={`ai-timeout-${c.user_id}`}
                      >
                        ⏱ IA timeout
                      </Badge>
                    )}
                    {c.extraction_status === "error" && (
                      <Badge className="rounded-none text-xs bg-red-100 text-red-700 border border-red-300" title="Erreur d'analyse IA — relancer depuis la fiche">
                        ⚠ IA erreur
                      </Badge>
                    )}
                    {c.extraction_status === "llm_unavailable" && (
                      <Badge className="rounded-none text-xs bg-purple-100 text-purple-700 border border-purple-300" title="Crédits Universal Key épuisés ou rate-limit — réessayer plus tard">
                        💸 IA crédit
                      </Badge>
                    )}
                    {(c.extraction_status === "llm_empty" || c.extraction_status === "ocr_empty") && (
                      <Badge className="rounded-none text-xs bg-gray-100 text-gray-600 border border-gray-300" title="L'IA n'a rien pu extraire des documents fournis">
                        ℹ IA vide
                      </Badge>
                    )}
                    {c.extraction_status === "applied" && (
                      <Badge className="rounded-none text-xs bg-green-100 text-green-700 border border-green-300" title="Données IA appliquées à la fiche">
                        ✓ IA OK
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    <span>{c.email}</span>
                    {c.phone && (
                      <>
                        {" | "}
                        <PhoneClickable value={c.phone} className="text-xs" data-testid={`row-phone-${c.user_id}`} />
                      </>
                    )}
                    {(c.postal_code || c.city) && (
                      <>
                        {" "}<span className="text-gray-400">·</span>{" "}
                        <span className="text-gray-600">{[c.postal_code, c.city].filter(Boolean).join(" ")}</span>
                      </>
                    )}
                    <span className="text-gray-400 ml-2">· Dernière connexion : <strong className={lastLogin ? "text-gray-600" : "text-amber-600"}>{lastLoginLabel}</strong></span>
                  </p>
                  {/* Inline agency attach — shown when candidate has no agency.
                      Lets the admin assign an agency in one click when they
                      have filtered by "Sans agence" for a bulk cleanup. */}
                  {!c.agency_id && agencies.length > 0 && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`quick-attach-agency-${c.user_id}`}>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Sans agence</span>
                      <Select onValueChange={async (v) => {
                        try {
                          await api.patch(`/candidates/${c.user_id}/profile`, { agency_id: v });
                          toast.success("Agence attribuée");
                          fetchCandidates();
                        } catch { toast.error("Erreur"); }
                      }}>
                        <SelectTrigger className="rounded-none h-7 text-xs border-amber-300 max-w-xs" data-testid={`quick-attach-agency-select-${c.user_id}`}>
                          <SelectValue placeholder="→ Rattacher à une agence…" />
                        </SelectTrigger>
                        <SelectContent>
                          {agencies.map((a) => (
                            <SelectItem key={a.agency_id} value={a.agency_id} data-testid={`quick-attach-opt-${c.user_id}-${a.agency_id}`}>
                              {a.name}{a.city ? ` — ${a.city}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleRole(c.user_id, c.role)} className="p-2 hover:bg-gray-100" title={c.role === "candidate" ? "Passer salarié" : "Passer candidat"} data-testid={`toggle-role-${c.user_id}`}>{c.role === "employee" ? <UserCheck className="w-4 h-4 text-[#662483]" /> : <UserX className="w-4 h-4 text-gray-400" />}</button>
                  <button onClick={() => toggleStatus(c.user_id, c.status)} className="p-2 hover:bg-gray-100" title={c.status === "active" ? "Désactiver" : "Activer"} data-testid={`toggle-status-candidate-${c.user_id}`}>{c.status === "active" ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}</button>
                  {c.role === "employee" && c.status !== "archived" ? (
                    <button onClick={() => archiveCandidate(c.user_id, c.name)} className="p-2 hover:bg-gray-100 text-amber-600" title="Archiver (conserver les données)" data-testid={`archive-candidate-${c.user_id}`}><Archive className="w-4 h-4" /></button>
                  ) : c.status === "archived" ? (
                    <button onClick={() => unarchiveCandidate(c.user_id)} className="p-2 hover:bg-gray-100 text-green-600" title="Réactiver" data-testid={`unarchive-candidate-${c.user_id}`}><ArchiveRestore className="w-4 h-4" /></button>
                  ) : null}
                  <button onClick={async () => { if (!window.confirm(`Supprimer définitivement ${c.name} ?\n\n⚠️ Action irréversible (RGPD). Pour les salariés, préférez l'archivage.`)) return; try { await api.delete(`/candidates/${c.user_id}`); toast.success("Candidat supprimé"); fetchCandidates(); } catch { toast.error("Erreur"); } }} className="p-2 hover:bg-gray-100 text-red-500" title="Supprimer définitivement" data-testid={`delete-candidate-${c.user_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          });
        })()}
        {candidates.length === 0 && <div className="text-center py-8 text-gray-500 text-sm">Aucun candidat inscrit</div>}
      </div>

      <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="sticky top-0 z-20 bg-white border-b border-gray-200 px-6 py-4 shrink-0">
            <DialogTitle className="text-[#3F3F3F] flex items-center gap-2 pr-8">
              <span className="truncate flex-1">Profil de {selectedCandidate?.name}</span>
              {selectedCandidate?.role === "employee" && <Badge className="rounded-none text-xs bg-[#F0E9F3] text-[#662483] shrink-0">Salarié</Badge>}
              {/* Prev/Next navigation within the currently filtered list — lets an admin
                  triage candidates one after another without closing the dialog. */}
              {currentFilteredIdx >= 0 && filteredCandidates.length > 1 && (
                <div className="hidden sm:flex items-center gap-1 shrink-0 ml-2" data-testid="candidate-nav-block">
                  <button
                    type="button"
                    onClick={() => prevCandidateId && viewCandidate(prevCandidateId)}
                    disabled={!prevCandidateId}
                    className="p-1 border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Candidat précédent (filtre courant)"
                    data-testid="candidate-nav-prev"
                  >
                    <ChevronLeft className="w-4 h-4 text-[#662483]" />
                  </button>
                  <span className="text-[10px] tabular-nums text-gray-500 font-semibold px-1" data-testid="candidate-nav-count">
                    {currentFilteredIdx + 1} / {filteredCandidates.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => nextCandidateId && viewCandidate(nextCandidateId)}
                    disabled={!nextCandidateId}
                    className="p-1 border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Candidat suivant (filtre courant)"
                    data-testid="candidate-nav-next"
                  >
                    <ChevronRight className="w-4 h-4 text-[#662483]" />
                  </button>
                </div>
              )}
            </DialogTitle>
            <DialogClose
              className="absolute right-4 top-4 z-30 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none p-1"
              data-testid="close-candidate-dialog"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-[#3F3F3F]" />
            </DialogClose>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-6 flex-1">
          {selectedCandidate && (
            <div className="space-y-6 mt-4">
              {/* Quick note — visible en haut pour un ajout instantané sans dérouler. */}
              <div className="flex items-center gap-2 bg-[#FFF8E1] border border-amber-200 p-2" data-testid="quick-note-block">
                <StickyNote className="w-4 h-4 text-amber-700 shrink-0" />
                <Input
                  value={quickNote}
                  onChange={(e) => setQuickNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && quickNote.trim()) { e.preventDefault(); addQuickNote(); } }}
                  placeholder="Note rapide (ex. « très motivé, dispo lundi »)…"
                  className="rounded-none border-amber-200 bg-white text-sm h-8 flex-1"
                  data-testid="quick-note-input"
                />
                <Button
                  onClick={addQuickNote}
                  disabled={!quickNote.trim()}
                  size="sm"
                  className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                  data-testid="quick-note-add-btn"
                >
                  <Save className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>

              {/* AI extraction preview — auto-fill from documents */}
              <AiExtractPanel
                userId={selectedCandidate.user_id}
                onApplied={() => viewCandidate(selectedCandidate.user_id)}
              />

              {/* Agence de rattachement — en haut pour visibilité */}
              <div className="bg-gradient-to-r from-[#F0E9F3] to-[#E6F0F7] border-l-4 border-[#662483] p-3" data-testid="agency-top-block">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#662483]" />
                    <Label className="text-xs font-bold text-[#662483] uppercase tracking-wider">Agence de rattachement</Label>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <Select value={candidateForm.agency_id || "__none__"} onValueChange={(v) => setCandidateForm({ ...candidateForm, agency_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="rounded-none text-sm h-9 bg-white" data-testid="edit-candidate-agency-top"><SelectValue placeholder="Aucune agence assignée" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Aucune agence —</SelectItem>
                        {agencies.map((a) => (<SelectItem key={a.agency_id} value={a.agency_id}>{a.name}{a.city ? ` — ${a.city}` : ""}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={saveCandidateProfile}
                    disabled={savingProfile}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-[#662483] text-[#662483] text-xs h-9"
                    data-testid="save-agency-top-btn"
                    title="Sauvegarder l'agence de rattachement"
                  >
                    <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
                  </Button>
                </div>
              </div>

              {/* Editable Candidate Info */}
              <div>
                <h4 className="text-sm font-bold text-[#3F3F3F] mb-3 flex items-center gap-2">
                  <Edit className="w-4 h-4" /> Informations du candidat
                  <Badge className={`rounded-none text-xs ml-2 ${selectedCandidate.role === "employee" ? "bg-[#F0E9F3] text-[#662483]" : "bg-[#E6F0F7] text-[#0069B4]"}`}>{selectedCandidate.role === "employee" ? "Salarié" : "Candidat"}</Badge>
                  <Badge className={`rounded-none text-xs ${selectedCandidate.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{selectedCandidate.status}</Badge>
                </h4>
                {selectedCandidate.created_at && (
                  <p className="text-[11px] text-gray-500 mb-3" data-testid="candidate-created-at">
                    Inscrit(e) le {new Date(selectedCandidate.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-gray-600 mb-1 block">Civilité *</Label>
                    <Select value={candidateForm.civility || ""} onValueChange={(v) => setCandidateForm({...candidateForm, civility: v})}>
                      <SelectTrigger className="rounded-none text-sm h-9 max-w-xs" data-testid="edit-candidate-civility-top"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monsieur">Monsieur</SelectItem>
                        <SelectItem value="madame">Madame</SelectItem>
                        <SelectItem value="mademoiselle">Mademoiselle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Prénom</Label><Input value={candidateForm.first_name || ""} onChange={(e) => setCandidateForm({...candidateForm, first_name: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-first-name" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Nom</Label><Input value={candidateForm.last_name || ""} onChange={(e) => setCandidateForm({...candidateForm, last_name: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-last-name" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Email</Label><Input value={candidateForm.email || ""} onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-email" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Téléphone</Label><PhoneInputFr value={candidateForm.phone || ""} onChange={(v) => setCandidateForm({...candidateForm, phone: v})} inputClassName="text-sm h-9" data-testid="edit-candidate-phone" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs font-semibold text-gray-600 mb-1 block">Adresse</Label><Input value={candidateForm.address || ""} onChange={(e) => setCandidateForm({...candidateForm, address: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-address" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Code postal</Label><Input value={candidateForm.postal_code || ""} onChange={(e) => setCandidateForm({...candidateForm, postal_code: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-postal" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Ville</Label><Input value={candidateForm.city || ""} onChange={(e) => setCandidateForm({...candidateForm, city: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-city" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Expérience</Label><Input value={candidateForm.experience || ""} onChange={(e) => setCandidateForm({...candidateForm, experience: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-experience" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Mobilité (km)</Label><Input type="number" value={candidateForm.mobility_km || ""} onChange={(e) => setCandidateForm({...candidateForm, mobility_km: parseInt(e.target.value) || 0})} className="rounded-none text-sm h-9" data-testid="edit-candidate-mobility" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Début disponibilité</Label><Input type="date" value={candidateForm.disponibilite_debut || ""} onChange={(e) => setCandidateForm({...candidateForm, disponibilite_debut: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-dispo-debut" /></div>
                  <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Fin disponibilité</Label><Input type="date" value={candidateForm.disponibilite_fin || ""} onChange={(e) => setCandidateForm({...candidateForm, disponibilite_fin: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-dispo-fin" /></div>
                </div>

                {/* État civil & Identité */}
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <h5 className="text-sm font-bold text-[#662483] mb-3">État civil</h5>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Nationalité</Label>
                      <NationalitySelect
                        value={candidateForm.nationality || ""}
                        onChange={(v) => setCandidateForm({ ...candidateForm, nationality: v })}
                        dataTestId="edit-candidate-nationality"
                      />
                    </div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Date de naissance</Label><Input type="date" value={candidateForm.birth_date || ""} onChange={(e) => setCandidateForm({...candidateForm, birth_date: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-birth-date" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Lieu de naissance</Label><Input value={candidateForm.birth_place || ""} onChange={(e) => setCandidateForm({...candidateForm, birth_place: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-birth-place" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Nom de naissance</Label><Input value={candidateForm.birth_name || ""} onChange={(e) => setCandidateForm({...candidateForm, birth_name: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-birth-name" /></div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Situation familiale</Label>
                      <Select value={candidateForm.marital_status || ""} onValueChange={(v) => setCandidateForm({...candidateForm, marital_status: v})}>
                        <SelectTrigger className="rounded-none text-sm h-9" data-testid="edit-candidate-marital"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="celibataire">Célibataire</SelectItem>
                          <SelectItem value="marie">Marié(e)</SelectItem>
                          <SelectItem value="pacs">Pacsé(e)</SelectItem>
                          <SelectItem value="divorce">Divorcé(e)</SelectItem>
                          <SelectItem value="veuf">Veuf(ve)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">N° Sécurité sociale</Label>
                      <SsnInput
                        value={candidateForm.social_security_number || ""}
                        onChange={(v) => setCandidateForm({...candidateForm, social_security_number: v})}
                        civility={candidateForm.civility || ""}
                        birthDateIso={candidateForm.birth_date || ""}
                        testid="edit-candidate-ssn"
                      />
                    </div>
                  </div>
                </div>

                {/* Pièce d'identité */}
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <h5 className="text-sm font-bold text-[#662483] mb-3">Pièce d'identité</h5>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Type de pièce</Label>
                      <Select value={candidateForm.id_doc_type || ""} onValueChange={(v) => setCandidateForm({...candidateForm, id_doc_type: v})}>
                        <SelectTrigger className="rounded-none text-sm h-9" data-testid="edit-candidate-id-type"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cni">CNI</SelectItem>
                          <SelectItem value="passeport">Passeport</SelectItem>
                          <SelectItem value="titre_sejour">Titre de séjour</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Date d'émission</Label><Input type="date" value={candidateForm.id_doc_validity_start || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_validity_start: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-id-start" /></div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">
                        Date d'expiration
                        {candidateForm.id_doc_validity_end && new Date(candidateForm.id_doc_validity_end) < new Date() && (
                          <span className="ml-2 text-red-500 text-[10px]">⚠️ Expirée</span>
                        )}
                      </Label>
                      <Input type="date" value={candidateForm.id_doc_validity_end || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_validity_end: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-id-end" />
                    </div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Pays d'émission</Label><Input value={candidateForm.id_doc_origin || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_origin: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-id-origin" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Département</Label><Input value={candidateForm.id_doc_department || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_department: e.target.value})} className="rounded-none text-sm h-9" data-testid="edit-candidate-id-department" /></div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Organisme émetteur</Label>
                      <Select value={candidateForm.id_doc_issuer_code || ""} onValueChange={(v) => setCandidateForm({...candidateForm, id_doc_issuer_code: v})}>
                        <SelectTrigger className="rounded-none text-sm h-9" data-testid="edit-candidate-id-issuer-code"><SelectValue placeholder="(défaut agence)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PP">PP — Préfecture de Police (Paris)</SelectItem>
                          <SelectItem value="PR">PR — Préfecture</SelectItem>
                          <SelectItem value="SP">SP — Sous-préfecture</SelectItem>
                          <SelectItem value="MA">MA — Mairie</SelectItem>
                          <SelectItem value="CO">CO — Commissariat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Département et lieu</Label><Input value={candidateForm.id_doc_issuer || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_issuer: e.target.value})} className="rounded-none text-sm h-9" placeholder="ex. Pref Isère" data-testid="edit-candidate-id-issuer" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Numéro de pièce d'identité</Label><Input value={candidateForm.id_doc_number || ""} onChange={(e) => setCandidateForm({...candidateForm, id_doc_number: e.target.value})} className="rounded-none text-sm h-9" placeholder="ex. YJ84LQDMF" data-testid="edit-candidate-id-number" /></div>
                  </div>
                  {/* Prefecture verification — only meaningful for foreign nationals */}
                  {isForeignNational({...selectedCandidate, ...candidateForm}) && (
                    <label className="flex items-start gap-2 cursor-pointer p-3 mt-3 bg-blue-50 border border-blue-200" data-testid="prefecture-verified-checkbox">
                      <Checkbox
                        checked={!!candidateForm.prefecture_verified}
                        onCheckedChange={(v) => setCandidateForm({...candidateForm, prefecture_verified: !!v})}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-[#3F3F3F]">🏛 Vérification préfecture effectuée</p>
                        <p className="text-[10px] text-gray-600 mt-0.5">À cocher une fois que les démarches administratives auprès de la préfecture ont été réalisées (consultation du fichier AGDREF, vérification de l'autorisation de travail, etc.) pour ce ressortissant étranger.</p>
                      </div>
                    </label>
                  )}
                </div>

                {/* Banque + Anael paie */}
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <h5 className="text-sm font-bold text-[#662483] mb-3">Coordonnées bancaires &amp; paie Anael</h5>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2"><Label className="text-xs font-semibold text-gray-600 mb-1 block">IBAN</Label><Input value={candidateForm.iban || ""} onChange={(e) => setCandidateForm({...candidateForm, iban: e.target.value.toUpperCase()})} className="rounded-none text-sm h-9 font-mono" placeholder="FR76 1234 5678 9012 3456 7890 123" data-testid="edit-candidate-iban" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">BIC</Label><Input value={candidateForm.bic || ""} onChange={(e) => setCandidateForm({...candidateForm, bic: e.target.value.toUpperCase()})} className="rounded-none text-sm h-9 font-mono" placeholder="BNPAFRPPXXX" data-testid="edit-candidate-bic" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Domiciliation</Label><Input value={candidateForm.bank_domiciliation || ""} onChange={(e) => setCandidateForm({...candidateForm, bank_domiciliation: e.target.value})} className="rounded-none text-sm h-9" placeholder="ex. BNP Paribas Paris 9e" data-testid="edit-candidate-bank-dom" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Qualification Anael (PERQUA)</Label><Input value={candidateForm.anael_qualification || ""} onChange={(e) => setCandidateForm({...candidateForm, anael_qualification: e.target.value})} className="rounded-none text-sm h-9" placeholder="(défaut agence)" data-testid="edit-candidate-anael-qua" /></div>
                    <div><Label className="text-xs font-semibold text-gray-600 mb-1 block">Catégorie Anael (PERCAT)</Label><Input value={candidateForm.anael_category || ""} onChange={(e) => setCandidateForm({...candidateForm, anael_category: e.target.value})} className="rounded-none text-sm h-9" placeholder="(défaut agence)" data-testid="edit-candidate-anael-cat" /></div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 italic">Les codes Qualification / Catégorie / Mode règlement sont définis par défaut au niveau de l'agence. Renseignez ici uniquement pour surcharger.</p>
                </div>
                {/* Personne à contacter en cas d'urgence */}
                <div className="mt-5 border-t border-gray-100 pt-4" data-testid="edit-candidate-emergency">
                  <h5 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> Contact d&apos;urgence
                  </h5>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Nom et prénom</Label>
                      <Input value={candidateForm.emergency_contact_name || ""} onChange={(e) => setCandidateForm({...candidateForm, emergency_contact_name: e.target.value})} className="rounded-none text-sm h-9" placeholder="ex. Marie DUPONT" data-testid="edit-candidate-emergency-name" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Téléphone</Label>
                      <PhoneInputFr value={candidateForm.emergency_contact_phone || ""} onChange={(v) => setCandidateForm({...candidateForm, emergency_contact_phone: v})} inputClassName="text-sm h-9" placeholder="06..." data-testid="edit-candidate-emergency-phone" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600 mb-1 block">Lien</Label>
                      <Select value={candidateForm.emergency_contact_relation || "__none__"} onValueChange={(v) => setCandidateForm({...candidateForm, emergency_contact_relation: v === "__none__" ? "" : v})}>
                        <SelectTrigger className="rounded-none text-sm h-9" data-testid="edit-candidate-emergency-relation"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          <SelectItem value="conjoint">Conjoint(e)</SelectItem>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="enfant">Enfant</SelectItem>
                          <SelectItem value="fratrie">Frère / Sœur</SelectItem>
                          <SelectItem value="ami">Ami(e)</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <h5 className="text-sm font-bold text-[#662483] mb-3">Consentements communication (RGPD)</h5>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer p-2 hover:bg-[#FAFAFA]" data-testid="consent-sms-operational">
                      <Checkbox
                        checked={!!candidateForm.sms_operational_consent}
                        onCheckedChange={(v) => setCandidateForm({...candidateForm, sms_operational_consent: !!v})}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-[#3F3F3F]">SMS opérationnels</p>
                        <p className="text-[10px] text-gray-500">Rappels de mission, confirmations, alertes urgentes (recommandé)</p>
                      </div>
                      <Badge className={`rounded-none text-[10px] ${candidateForm.sms_operational_consent ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {candidateForm.sms_operational_consent ? "✓ Activé" : "✗ Refusé"}
                      </Badge>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer p-2 hover:bg-[#FAFAFA]" data-testid="consent-sms-marketing">
                      <Checkbox
                        checked={!!candidateForm.sms_marketing_consent}
                        onCheckedChange={(v) => setCandidateForm({...candidateForm, sms_marketing_consent: !!v})}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-[#3F3F3F]">SMS marketing</p>
                        <p className="text-[10px] text-gray-500">Nouvelles offres, promos. Nécessite consentement explicite (opt-in)</p>
                      </div>
                      <Badge className={`rounded-none text-[10px] ${candidateForm.sms_marketing_consent ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {candidateForm.sms_marketing_consent ? "✓ Activé" : "✗ Refusé"}
                      </Badge>
                    </label>
                    <p className="text-[10px] text-gray-400 italic px-2">L'email RGPD est lié à l'inscription (rgpd_consent) et ne peut être modifié ici.</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <StickyNote className="w-4 h-4 text-[#662483]" />
                    <Label className="text-sm font-bold text-[#662483]">Notes personnelles (non visibles par le candidat)</Label>
                  </div>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="rounded-none min-h-[100px] text-sm"
                    placeholder="Ajoutez vos notes ici..."
                    data-testid="admin-notes-textarea"
                  />
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer border border-gray-200 p-2 hover:bg-[#FAFAFA]" data-testid="chk-epi-test">
                      <Checkbox
                        checked={!!candidateForm.epi_safety_test_ok}
                        onCheckedChange={(v) => setCandidateForm({...candidateForm, epi_safety_test_ok: !!v, epi_distributed_date: !v ? "" : (candidateForm.epi_distributed_date || new Date().toISOString().slice(0,10))})}
                      />
                      <HardHat className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-semibold text-[#3F3F3F]">EPI Distribué</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer border border-gray-200 p-2 hover:bg-[#FAFAFA]" data-testid="chk-references-check">
                      <Checkbox
                        checked={!!candidateForm.references_check_ok}
                        onCheckedChange={(v) => setCandidateForm({...candidateForm, references_check_ok: !!v})}
                      />
                      <span className="text-xs font-semibold text-[#3F3F3F]">📞 Contrôles de références effectués</span>
                    </label>
                  </div>
                  {candidateForm.epi_safety_test_ok && (
                    <div className="mt-2 flex items-center gap-2 border border-amber-200 bg-amber-50 p-2" data-testid="epi-date-block">
                      <CalendarDays className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                      <Label className="text-xs font-semibold text-amber-800">Date de distribution</Label>
                      <Input
                        type="date"
                        value={candidateForm.epi_distributed_date || ""}
                        onChange={(e) => setCandidateForm({...candidateForm, epi_distributed_date: e.target.value})}
                        className="rounded-none text-sm h-8 flex-1 max-w-[200px]"
                        data-testid="epi-distributed-date"
                      />
                    </div>
                  )}
                </div>
                <Button onClick={saveCandidateProfile} disabled={savingProfile} className="mt-3 bg-[#0069B4] text-white rounded-none font-semibold text-sm" data-testid="save-candidate-profile-btn">
                  <Save className="w-3 h-3 mr-2" /> {savingProfile ? "Sauvegarde..." : "Enregistrer le profil"}
                </Button>
              </div>

              {/* Compliance + inline document actions.
                  - Cliquer sur une tuile déjà déposée → ouvre le split-preview
                  - Cliquer sur une tuile "Manquant" → ouvre le dialog upload
                    pré-rempli avec le type de doc concerné
                  - Bouton "Déposer un document" dans l'en-tête pour tout ajout libre */}
              <ComplianceChecklist
                docTypes={(selectedCandidate.documents || []).map((d) => d.doc_type)}
                documents={selectedCandidate.documents || []}
                role={selectedCandidate.role}
                isAdmin={true}
                onPreviewDoc={(doc) => setPreviewDoc(doc)}
                onRequestUpload={(docType) => uploadDocRef.current?.openWith(docType)}
                onRenameDoc={(doc) => setRenameDoc(doc)}
                onArchiveDoc={async (doc) => {
                  if (!window.confirm(`Archiver « ${doc.original_filename} » ?\n\nLe document sera masqué mais restera restaurable.`)) return;
                  try {
                    await api.post(`/documents/${doc.doc_id}/archive`);
                    toast.success("Document archivé");
                    viewCandidate(selectedCandidate.user_id);
                  } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                }}
                onDeleteDoc={async (doc) => {
                  if (!window.confirm(`Supprimer définitivement « ${doc.original_filename} » ?\n\n⚠️ Action irréversible. Le fichier sera effacé du stockage.`)) return;
                  try {
                    await api.delete(`/documents/${doc.doc_id}`);
                    toast.success("Document supprimé");
                    viewCandidate(selectedCandidate.user_id);
                  } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                }}
                headerAction={
                  <AdminUploadDocButton
                    ref={uploadDocRef}
                    userId={selectedCandidate.user_id}
                    role={selectedCandidate.role}
                    existingTypes={(selectedCandidate.documents || []).map((d) => d.doc_type)}
                    onUploaded={() => viewCandidate(selectedCandidate.user_id)}
                  />
                }
              />
              {(() => {
                const rejectedCount = (selectedCandidate.documents || []).filter((d) => d.validation_status === "rejected").length;
                if (rejectedCount === 0) return null;
                return (
                  <div className="border border-red-200 bg-red-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="notify-rejected-block">
                    <div className="text-xs text-red-800 min-w-0 flex-1">
                      <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                      <strong>{rejectedCount}</strong> pièce{rejectedCount > 1 ? "s" : ""} refusée{rejectedCount > 1 ? "s" : ""}. Notifiez le candidat par email avec les motifs.
                    </div>
                    <Button
                      onClick={() => setRejectEmailOpen(true)}
                      size="sm"
                      className="rounded-none bg-red-600 hover:bg-red-700 text-white text-xs"
                      data-testid="notify-rejected-btn"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Envoyer email refus
                    </Button>
                  </div>
                );
              })()}
              {(() => {
                const comp = computeCompliance((selectedCandidate.documents || []).map((d) => d.doc_type), selectedCandidate.role);
                const missing = comp.provided < comp.total;
                if (!missing) return null;
                return (
                  <div className="border border-orange-200 bg-orange-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="reclaim-docs-block">
                    <div className="text-xs text-orange-800 min-w-0 flex-1">
                      <strong>{comp.total - comp.provided}</strong> document(s) obligatoire(s) manquant(s). Vous pouvez envoyer un email au candidat pour lui demander de les déposer.
                    </div>
                    <Button
                      onClick={async () => {
                        if (!window.confirm("Envoyer un email de rappel au candidat listant les pièces manquantes ?")) return;
                        try {
                          const r = await api.post(`/admin/candidates/${selectedCandidate.user_id}/request-missing-documents`);
                          if (r.data?.sent) {
                            toast.success(`Email envoyé (${r.data.count} pièce(s) demandée(s))`);
                          } else {
                            toast.info(r.data?.reason || "Rien à réclamer");
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Erreur");
                        }
                      }}
                      size="sm"
                      className="rounded-none bg-orange-500 hover:bg-orange-600 text-white text-xs"
                      data-testid="reclaim-docs-btn"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Réclamer les documents
                    </Button>
                  </div>
                );
              })()}

              {/* CACES Checkboxes + Expiry dates */}
              <CandidateCacesCard
                caces={adminCaces}
                cacesExpiry={adminCacesExpiry}
                onCacesChange={setAdminCaces}
                onCacesExpiryChange={setAdminCacesExpiry}
              />

              {/* Save Button */}
              <Button
                onClick={saveAdminInfo}
                disabled={savingInfo}
                className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold"
                data-testid="save-admin-info-btn"
              >
                <Save className="w-4 h-4 mr-2" /> {savingInfo ? "Sauvegarde..." : "Sauvegarder les informations admin"}
              </Button>

              {/* Email Actions */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-bold text-[#0069B4] mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Actions email</h4>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="rounded-none border-[#662483] text-[#662483] text-xs" data-testid="email-relance-btn"
                    onClick={async () => {
                      try { await api.post("/admin/email/relance", { user_id: selectedCandidate.user_id }); toast.success("Email de relance envoyé"); } catch { toast.error("Erreur d'envoi"); }
                    }}>
                    Relance candidat inactif
                  </Button>
                  <MissionEmailButton candidateId={selectedCandidate.user_id} />
                  {(() => {
                    const docTypes = (selectedCandidate.documents || []).map((d) => d.doc_type);
                    const missing = computeCompliance(docTypes, selectedCandidate.role).missing;
                    return missing.length > 0 ? (
                      <Button variant="outline" className="rounded-none border-amber-600 text-amber-700 text-xs" data-testid="email-missing-docs-btn"
                        onClick={async () => {
                          try {
                            await api.post("/admin/email/missing-documents", { user_id: selectedCandidate.user_id, missing });
                            toast.success(`Email envoyé : ${missing.length} document(s) demandé(s)`);
                          } catch { toast.error("Erreur d'envoi"); }
                        }}>
                        Demander les pièces manquantes ({missing.length})
                      </Button>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Historique missions timeline */}
              <CandidateMissionsTimeline userId={selectedCandidate.user_id} candidateName={selectedCandidate.name} />

              {/* Anael TT sync */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-bold text-[#662483] mb-2 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Synchronisation Anael TT</h4>
                {selectedCandidate.anael_matricule ? (
                  <p className="text-xs text-gray-600 mb-2">Matricule Anael : <span className="font-mono font-bold text-[#3F3F3F]">{selectedCandidate.anael_matricule}</span> {selectedCandidate.anael_synced_at && <span className="text-gray-400">(dernière synchro : {new Date(selectedCandidate.anael_synced_at).toLocaleString("fr-FR")})</span>}</p>
                ) : (
                  <p className="text-xs text-gray-500 mb-2">Pas encore synchronisé avec Anael TT.</p>
                )}
                <Button variant="outline" className="rounded-none border-[#662483] text-[#662483] text-xs" data-testid="anael-sync-btn"
                  onClick={async () => {
                    try {
                      const r = await api.post("/admin/anael/sync-employee", { user_id: selectedCandidate.user_id });
                      const d = r.data;
                      if (d.status === "success") toast.success(`✅ Synchro Anael OK — matricule ${d.matricule}`);
                      else if (d.status === "simulated") toast.success(`🧪 Simulé (URL Anael non configurée) — matricule fictif ${d.matricule}`);
                      else if (d.status === "validation_error") toast.error(`Champs manquants : ${d.missing.join(", ")}`);
                      else toast.error(`Anael KO : ${d.message}`);
                    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                  }}>
                  {selectedCandidate.anael_matricule ? "Resynchroniser dans Anael TT" : "Envoyer dans Anael TT"}
                </Button>
              </div>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
      <CandidateSplitPreviewDialog
        candidate={selectedCandidate}
        doc={previewDoc}
        open={!!previewDoc}
        onOpenChange={(v) => !v && setPreviewDoc(null)}
        onSaved={(updated) => {
          if (!updated) return;
          setSelectedCandidate((c) => (c ? { ...c, ...updated } : c));
          // Sync candidateForm so a subsequent "Save profile" won't overwrite
          // fields just modified live in the split preview panel.
          setCandidateForm((f) => ({ ...f, ...updated }));
        }}
      />
      <CVAnonymizer
        doc={anonymizeDoc}
        candidateName={selectedCandidate?.name || "candidat"}
        open={!!anonymizeDoc}
        onOpenChange={(v) => !v && setAnonymizeDoc(null)}
        onSaved={() => {
          // Rafraîchit la fiche candidat pour faire apparaître le nouveau
          // document "ANON - ..." dans la liste sans action manuelle.
          if (selectedCandidate?.user_id) viewCandidate(selectedCandidate.user_id);
        }}
      />
      <EditDocumentDialog
        doc={renameDoc}
        onOpenChange={(v) => !v && setRenameDoc(null)}
        onUpdated={(updated) => {
          if (selectedCandidate?.documents) {
            const idx = selectedCandidate.documents.findIndex((d) => d.doc_id === updated.doc_id);
            if (idx >= 0) {
              const nextDocs = [...selectedCandidate.documents];
              nextDocs[idx] = { ...nextDocs[idx], ...updated };
              setSelectedCandidate({ ...selectedCandidate, documents: nextDocs });
            }
          }
        }}
      />
      <RejectedDocsEmailDialog
        candidate={selectedCandidate}
        open={rejectEmailOpen}
        onOpenChange={setRejectEmailOpen}
        onSent={() => selectedCandidate && viewCandidate(selectedCandidate.user_id)}
      />
      <CreateCandidateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(user, openProfile) => {
          fetchCandidates();
          if (openProfile && user?.user_id) {
            viewCandidate(user.user_id);
          }
        }}
      />
    </div>
  );
}
